import React, { useEffect, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { ShowcaseCutClips } from '../../showcase/ShowcaseCutClips';
import { sectionAt } from '../../timeline/timelineGeometry';
import { BankResults } from './BankResults';
import { BankSuggestions } from './BankSuggestions';
import { BANK_FILTER_CHIPS, bankQueryOf, isBankSearch, toggleBankChip, type BankFilterChip } from './bankFilters';
import { YoursPreview } from './YoursPreview';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const CHIP_MESSAGES = {
  noPeople: footageMessages.chipNoPeople,
  night: footageMessages.chipNight,
  nature: footageMessages.chipNature,
  calm: footageMessages.chipChill,
  euphoric: footageMessages.chipEuphoric,
} as const;

/** The wait after the last key before the search goes out. */
const SEARCH_DELAY_MS = 300;

/**
 * The Bank tab (design b1): a search, the filter chips and "Fits <section>", the clips the director
 * would pick for the section (`GET /footage/bank/suggest`), a look at Yours. A search or a filter
 * shows the matching clips of the bank (`GET /footage/bank`). In showcase mode the clips of the cut
 * follow ("In this cut", design c1) in place of Yours.
 */
const BankTabComponent: React.FC<{ onShowYours: () => void }> = ({ onShowYours }) => {
  const intl = useIntl();
  const searchId = useId();
  const { songMap, sectionIndex, transport, mode } = useMusicVideoEditor();
  const [text, setText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [chips, setChips] = useState<BankFilterChip[]>([]);
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchText(text), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text]);
  const query = useMemo(() => bankQueryOf(searchText, chips), [chips, searchText]);
  const section = useMemo(
    () =>
      songMap.sections.find((item) => item.index === sectionIndex) ??
      sectionAt(songMap.sections, transport.positionSec),
    [sectionIndex, songMap.sections, transport.positionSec]
  );

  return (
    <>
      <label className={styles.search} htmlFor={searchId}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <circle cx="11" cy="11" r="6.5" />
          <path d="M20 20l-4-4" />
        </svg>
        <input
          id={searchId}
          type="search"
          value={text}
          placeholder={intl.formatMessage(footageMessages.bankSearch)}
          aria-label={intl.formatMessage(footageMessages.bankSearch)}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      <div className={styles.chips}>
        {section !== null && !isBankSearch(query) && (
          <span className={clsx(styles.chip, styles.chipOn)}>
            {intl.formatMessage(footageMessages.chipFits, { section: section.label })}
          </span>
        )}
        {BANK_FILTER_CHIPS.map((chip) => (
          <button
            key={chip}
            type="button"
            className={clsx(styles.chip, chips.includes(chip) && styles.chipOn)}
            aria-pressed={chips.includes(chip)}
            onClick={() => setChips((picked) => toggleBankChip(picked, chip))}
          >
            {intl.formatMessage(CHIP_MESSAGES[chip])}
          </button>
        ))}
      </div>
      {isBankSearch(query) ? <BankResults query={query} /> : section !== null && <BankSuggestions section={section} />}
      {mode === 'showcase' ? <ShowcaseCutClips /> : <YoursPreview onShowAll={onShowYours} />}
    </>
  );
};

export const BankTab = TypedMemo(BankTabComponent);
